import React, { useState, useRef, useCallback, useEffect } from 'react';
import { readPngInfo } from '../utils/pngInfo';
import { Contour, ContourPoint, MACHINE_TYPES } from '../types';
import { extractContours } from '../utils/vectorize';

// ── PNG helpers ────────────────────────────────────────────────

function readU32BE(a: Uint8Array, i: number): number {
  return ((a[i] << 24) | (a[i+1] << 16) | (a[i+2] << 8) | a[i+3]) >>> 0;
}
function writeU32BE(a: Uint8Array, i: number, v: number): void {
  a[i] = (v >>> 24) & 0xFF; a[i+1] = (v >>> 16) & 0xFF;
  a[i+2] = (v >>> 8) & 0xFF; a[i+3] = v & 0xFF;
}
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c;
  }
  return t;
})();
function crc32(data: Uint8Array): number {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
}
function setPngDpi(png: Uint8Array, ppmX: number, ppmY: number): Uint8Array {
  let pos = 8; let ihdrEnd = 33; let pHYsStart = -1; let pHYsEnd = -1;
  while (pos + 12 <= png.length) {
    const len  = readU32BE(png, pos);
    const type = String.fromCharCode(png[pos+4], png[pos+5], png[pos+6], png[pos+7]);
    const end  = pos + 12 + len;
    if (type === 'IHDR') ihdrEnd = end;
    if (type === 'pHYs') { pHYsStart = pos; pHYsEnd = end; }
    if (type === 'IEND') break;
    pos = end;
  }
  const data = new Uint8Array(9);
  writeU32BE(data, 0, ppmX); writeU32BE(data, 4, ppmY); data[8] = 1;
  const typeBytes = new Uint8Array([0x70, 0x48, 0x59, 0x73]);
  const crcInput  = new Uint8Array(13);
  crcInput.set(typeBytes, 0); crcInput.set(data, 4);
  const chunk = new Uint8Array(21);
  writeU32BE(chunk, 0, 9); chunk.set(typeBytes, 4); chunk.set(data, 8);
  writeU32BE(chunk, 17, crc32(crcInput));
  const insertAt  = pHYsStart !== -1 ? pHYsStart : ihdrEnd;
  const removeEnd = pHYsStart !== -1 ? pHYsEnd   : ihdrEnd;
  const result = new Uint8Array(png.length - (removeEnd - insertAt) + chunk.length);
  result.set(png.subarray(0, insertAt));
  result.set(chunk, insertAt);
  result.set(png.subarray(removeEnd), insertAt + chunk.length);
  return result;
}
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64.replace(/\s/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── Image canvas helpers ───────────────────────────────────────

function loadImg(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

async function rescaleToCanvas(dataUrl: string, w: number, h: number): Promise<string> {
  const img = await loadImg(dataUrl);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d')!.drawImage(img, 0, 0, w, h);
  return c.toDataURL('image/png');
}

// ── Klasyfikacja pikseli (identyczna logika jak vectorize.ts) ─
//   0 = czarny (materiał), 1 = biały (otwór), 2 = czerwony (do wycięcia)
//   Próg czerwonego: R≥128 && G<100 && B<100 (obejmuje blendy bilinearne
//   czerwono-czarne, np. R=140 G=5 B=5, które przy R≥150 wpadałyby w klasę białą)

function classifyPixel(r: number, g: number, b: number, a: number): 0 | 1 | 2 {
  if (a < 128) return 2;                           // przezroczysty → czerwony
  if (r >= 128 && g < 100 && b < 100) return 2;   // czerwony
  if (r >= 128) return 1;                          // biały
  return 0;                                        // czarny
}

// ── Normalizacja kolorów → czarno-biały ──────────────────────
// Używa tych samych progów co classifyPixel:
//   klasa 1 (biały otwór) → biały
//   klasa 0 (czarny materiał) → czarny
//   klasa 2 (czerwone wycięcie) → czarny
// Czerwony kolor trafia do czarnego – zamierzony efekt: po "To B-W" cały
// obszar poza otworem staje się czarnym materiałem.

async function normalizeToBlackWhite(dataUrl: string): Promise<string> {
  const img = await loadImg(dataUrl);
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, c.width, c.height);
  const d  = id.data;
  for (let i = 0; i < d.length; i += 4) {
    const white = classifyPixel(d[i], d[i+1], d[i+2], d[i+3]) === 1;
    d[i] = d[i+1] = d[i+2] = white ? 255 : 0;
    d[i+3] = 255;
  }
  ctx.putImageData(id, 0, 0);
  return c.toDataURL('image/png');
}

// ── Normalizacja kolorów → czerwony-czarny-biały ─────────────
// Używa tych samych progów co vectorize.ts (patrz classifyPixel powyżej).

async function normalizeToRedBlackWhite(dataUrl: string): Promise<string> {
  const img = await loadImg(dataUrl);
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  const d  = id.data;
  for (let i = 0; i < w * h; i++) {
    const cls = classifyPixel(d[i*4], d[i*4+1], d[i*4+2], d[i*4+3]);
    if (cls === 2) { d[i*4]=255; d[i*4+1]=0;   d[i*4+2]=0;   }  // czerwony
    else if (cls === 1) { d[i*4]=255; d[i*4+1]=255; d[i*4+2]=255; }  // biały
    else { d[i*4]=0;   d[i*4+1]=0;   d[i*4+2]=0;   }  // czarny
    d[i*4+3] = 255;
  }
  ctx.putImageData(id, 0, 0);
  return c.toDataURL('image/png');
}

// ── Dokładna euklidesowa transformata odległości (EDT) ────────
// Algorytm Felzenszwalba-Huttenlochera (2012), O(w*h).
// Izolinie odległości są PRAWDZIWYMI okręgami (brak artefaktów oktagonalnych
// charakterystycznych dla aproksymacji Chamfera).
//
// Faza 1: dla każdego wiersza obliczamy kwadrat odległości do najbliższego
//         białego piksela w tym samym wierszu (dwa przebiegi L→P i P→L).
// Faza 2: dla każdej kolumny minimalizujemy (y-q)² + rowSqDist[q] za pomocą
//         dolnej obwiedni parabolicznej (ang. „lower envelope of parabolas").
//         Złożoność liniowa dzięki właściwości monotoniczności przecięć.
// Wynik:  dokładna odległość euklidesowa (w pikselach) do najbliższego otworu.

function euclideanDistanceTransform(whiteMask: Uint8Array, w: number, h: number): Float32Array {
  const INF = 1e10;   // > (2000² + 2000²) = 8e6, wystarczy dla każdego obrazu

  // ── Faza 1: kwadrat odległości poziomej (w każdym wierszu) ──
  const rowSqDist = new Float64Array(w * h);

  for (let y = 0; y < h; y++) {
    const base = y * w;
    // Przebieg L→P: odległość do ostatniego białego po lewej
    let lastW = -1;
    for (let x = 0; x < w; x++) {
      if (whiteMask[base + x]) lastW = x;
      rowSqDist[base + x] = lastW < 0 ? INF : (x - lastW) * (x - lastW);
    }
    // Przebieg P→L: minimum z odległością do najbliższego białego po prawej
    let nextW = -1;
    for (let x = w - 1; x >= 0; x--) {
      if (whiteMask[base + x]) nextW = x;
      if (nextW >= 0) {
        const dR = (x - nextW) * (x - nextW);
        if (dR < rowSqDist[base + x]) rowSqDist[base + x] = dR;
      }
    }
  }

  // ── Faza 2: dolna obwiednia paraboliczna wzdłuż kolumn ──────
  // Dla kolumny x szukamy: result[y] = sqrt( min_q { (y-q)² + f[q] } )
  // gdzie f[q] = rowSqDist[q][x].
  //
  // Kluczowa właściwość (FH 2012): przecięcia kolejnych parabolid są
  // monotoniczne, więc dolną obwiednię można zbudować jednym przebiegiem.
  // Gwarancja zakończenia pętli while: z[0] = -INF < każde skończone s.

  const result = new Float32Array(w * h);
  const v = new Int32Array(h);       // indeksy wierzchołków aktywnych paraboloid
  const z = new Float64Array(h + 1); // punkty przecięcia sąsiednich paraboloid
  const f = new Float64Array(h);

  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = rowSqDist[y * w + x];

    let k = 0;
    v[0] = 0;
    z[0] = -INF;
    z[1] = INF;

    for (let q = 1; q < h; q++) {
      // Punkt przecięcia paraboloidy v[k] i nowej paraboloidy q:
      //   s = ( (f[q]+q²) - (f[v[k]]+v[k]²) ) / ( 2q - 2v[k] )
      let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * (q - v[k]));
      while (s <= z[k]) {
        // Paraboloida v[k] jest zdominowana – usuń ze stosu
        k--;
        s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * (q - v[k]));
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = INF;
    }

    // Skan: przypisz każdemu y odległość od najbliższej paraboloidy
    k = 0;
    for (let y = 0; y < h; y++) {
      while (z[k + 1] < y) k++;
      const r = v[k];
      result[y * w + x] = Math.sqrt((y - r) * (y - r) + f[r]);
    }
  }

  return result;
}

// ── Tworzenie kołnierza (flange) ──────────────────────────────
// Algorytm:
//  1. Sprowadź obraz do dwóch kolorów: biały (otwór) i czerwony (reszta).
//     Czarny → czerwony, bo kołnierz zaczyna się od stanu „tylko wycięcie + otwór".
//  2. Oblicz dokładną euklidesową transformatę odległości (EDT) od białych pikseli.
//  3. Czerwone piksele w odległości ≤ flangeWidthPx od otworu → czarne (kołnierz).
//  4. Białe piksele (otwór) pozostają białe.
//  Wynik: szablon trójkolorowy R-B-W z czarnym kołnierzem wokół otworu.

async function createFlange(dataUrl: string, flangeWidthPx: number): Promise<string> {
  const img = await loadImg(dataUrl);
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  const d  = id.data;

  // Krok 1: wyznacz maskę białych pikseli (otwór szablonu)
  // Używamy tej samej klasyfikacji co vectorize.ts – otwór = klasa 1
  const whiteMask = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    whiteMask[i] = classifyPixel(d[i*4], d[i*4+1], d[i*4+2], d[i*4+3]) === 1 ? 1 : 0;
  }

  // Krok 2: dokładna EDT od otworu (algorytm FH 2012 – prawdziwe okręgi)
  const dist = euclideanDistanceTransform(whiteMask, w, h);

  // Krok 3: przypisz kolory
  //   dist = 0          → biały (sam otwór)
  //   0 < dist ≤ r      → czarny (kołnierz)
  //   dist > r          → czerwony (obszar do wycięcia)
  for (let i = 0; i < w * h; i++) {
    if (whiteMask[i]) {
      d[i*4]=255; d[i*4+1]=255; d[i*4+2]=255;  // otwór – biały
    } else if (dist[i] <= flangeWidthPx) {
      d[i*4]=0;   d[i*4+1]=0;   d[i*4+2]=0;    // kołnierz – czarny
    } else {
      d[i*4]=255; d[i*4+1]=0;   d[i*4+2]=0;    // tło – czerwone
    }
    d[i*4+3] = 255;
  }

  ctx.putImageData(id, 0, 0);
  return c.toDataURL('image/png');
}

// ── Etykietowanie spójnych białych obszarów (4-connectivity DFS) ─
// Zwraca:
//   labels     – tablica w * h; labels[i] = numer komponentu lub -1 (nie-biały)
//   components – posortowana malejąco lista { label, size } (rozmiar w pikselach)
//
// Używa DFS z jawnym stosem zamiast rekurencji (obrazy 2000×2000 = 4 Mpx).
// 4-connectivity zapewnia spójność odpowiadającą narzędziom flood-fill.

interface WhiteComp { label: number; size: number; }

function labelWhiteComponents(
  d: Uint8ClampedArray | Uint8Array, w: number, h: number
): { labels: Int32Array; components: WhiteComp[] } {
  const labels = new Int32Array(w * h).fill(-1);
  const components: WhiteComp[] = [];
  let nextLabel = 0;

  for (let i = 0; i < w * h; i++) {
    if (labels[i] !== -1) continue;
    if (classifyPixel(d[i*4], d[i*4+1], d[i*4+2], d[i*4+3]) !== 1) continue;

    const label = nextLabel++;
    let size = 0;
    const stack: number[] = [i];
    labels[i] = label;

    while (stack.length > 0) {
      const idx = stack.pop()!;
      size++;
      const x = idx % w;
      const y = (idx / w) | 0;
      // 4 sąsiedzi: prawo, lewo, dół, góra
      if (x + 1 < w)  { const ni = idx + 1; if (labels[ni] === -1 && classifyPixel(d[ni*4], d[ni*4+1], d[ni*4+2], d[ni*4+3]) === 1) { labels[ni] = label; stack.push(ni); } }
      if (x - 1 >= 0) { const ni = idx - 1; if (labels[ni] === -1 && classifyPixel(d[ni*4], d[ni*4+1], d[ni*4+2], d[ni*4+3]) === 1) { labels[ni] = label; stack.push(ni); } }
      if (y + 1 < h)  { const ni = idx + w; if (labels[ni] === -1 && classifyPixel(d[ni*4], d[ni*4+1], d[ni*4+2], d[ni*4+3]) === 1) { labels[ni] = label; stack.push(ni); } }
      if (y - 1 >= 0) { const ni = idx - w; if (labels[ni] === -1 && classifyPixel(d[ni*4], d[ni*4+1], d[ni*4+2], d[ni*4+3]) === 1) { labels[ni] = label; stack.push(ni); } }
    }
    components.push({ label, size });
  }

  // Sortuj malejąco po rozmiarze (największy otwór pierwszy)
  components.sort((a, b) => b.size - a.size);
  return { labels, components };
}

// Liczy spójne białe obszary w dataUrl (do wpisania w pole "leaveHoles").
async function countHolesInUrl(dataUrl: string): Promise<number> {
  const img = await loadImg(dataUrl);
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, c.width, c.height).data;
  const { components } = labelWhiteComponents(d, c.width, c.height);
  return components.length;
}

// ── Ekstrakcja konturów z bieżącego obrazu roboczego ──────────
// Używa tych samych klas pikseli co classifyPixel:
//   whiteMask   → kontury otworów (białe regiony)
//   combinedMask → zewnętrzny kontur kołnierza (czarny+biały vs. czerwony)
//                  generowany tylko gdy hasBlack && hasRed (= jest kołnierz)
// Skala: scaleX/Y = mm/px; origin = środek obrazu.

async function extractImageContours(
  dataUrl: string, imgWidthMm: number, imgHeightMm: number,
): Promise<{ holeContours: Contour[]; outerContours: Contour[] }> {
  const img = await loadImg(dataUrl);
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d')!.drawImage(img, 0, 0);
  const d = c.getContext('2d')!.getImageData(0, 0, w, h).data;

  const whiteMask    = new Uint8Array(w * h);
  const combinedMask = new Uint8Array(w * h);
  let hasBlack = false, hasRed = false;
  for (let i = 0; i < w * h; i++) {
    const cls = classifyPixel(d[i*4], d[i*4+1], d[i*4+2], d[i*4+3]);
    whiteMask[i]    = cls === 1 ? 1 : 0;
    combinedMask[i] = (cls === 0 || cls === 1) ? 1 : 0;
    if (cls === 0) hasBlack = true;
    if (cls === 2) hasRed   = true;
  }

  const scaleX = imgWidthMm / w;
  const scaleY = imgHeightMm / h;
  const ocx = w / 2, ocy = h / 2;

  const holeContours  = extractContours(whiteMask, w, h, scaleX, scaleY, ocx, ocy);
  // Zewnętrzny kontur kołnierza: tylko gdy są piksele czarne (kołnierz) I czerwone (tło cięcia).
  // filterBorder=true: pomija wyspy dotykające brzegu obrazu (ich granica = krawędź obrazu,
  // już zaznaczona jako "ramka szablonu").
  const outerContours = (hasBlack && hasRed)
    ? extractContours(combinedMask, w, h, scaleX, scaleY, ocx, ocy, true)
    : [];

  return { holeContours, outerContours };
}

// ── Generowanie SVG ───────────────────────────────────────────
// Układ współrzędnych SVG: origin = lewy-górny, Y w dół.
// ContourPoint: origin = środek obrazu, Y w górę.
// Transformacja: svgX = cx + mmX,  svgY = cy - mmY  (gdzie cx/cy = środek viewboxa)
//
// Warstwy:
//   czarny – ramka szablonu + kontury otworów
//   czerwony – zewnętrzny kontur kołnierza (jeśli jest)
//   zielony – ramka maszyny + otwory na śruby (jeśli Frame włączony)
//
// Grubość linii: 0.5 mm.

function buildSVG(
  holeContours:  Contour[],
  outerContours: Contour[],
  imgWidthMm:    number,
  imgHeightMm:   number,
  machWidthMm:   number,
  machHeightMm:  number,
  frameEnabled:  boolean,
): string {
  const SW   = 0.5;
  const viewW = frameEnabled ? Math.max(imgWidthMm, machWidthMm) : imgWidthMm;
  const viewH = frameEnabled ? Math.max(imgHeightMm, machHeightMm) : imgHeightMm;
  const cx = viewW / 2;   // środek obrazu w SVG
  const cy = viewH / 2;

  const sx = (x: number) => (cx + x).toFixed(3);
  const sy = (y: number) => (cy - y).toFixed(3);
  const polyPts = (pts: ContourPoint[]) => pts.map(p => `${sx(p.x)},${sy(p.y)}`).join(' ');

  let s  = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  s += `<svg xmlns="http://www.w3.org/2000/svg" `;
  s += `width="${viewW}mm" height="${viewH}mm" viewBox="0 0 ${viewW} ${viewH}">\n`;

  // Ramka szablonu (czarna)
  const fw = imgWidthMm, fh = imgHeightMm;
  s += `  <rect x="${sx(-fw/2)}" y="${sy(fh/2)}" width="${fw}" height="${fh}" `;
  s += `fill="none" stroke="black" stroke-width="${SW}"/>\n`;

  // Kontury otworów (czarne)
  for (const c of holeContours)
    s += `  <polygon points="${polyPts(c.points)}" fill="none" stroke="black" stroke-width="${SW}"/>\n`;

  // Zewnętrzny kontur kołnierza (czerwony)
  for (const c of outerContours)
    s += `  <polygon points="${polyPts(c.points)}" fill="none" stroke="red" stroke-width="${SW}"/>\n`;

  // Ramka maszyny + otwory na śruby (zielone)
  if (frameEnabled) {
    const mw = machWidthMm, mh = machHeightMm;
    s += `  <rect x="${sx(-mw/2)}" y="${sy(mh/2)}" width="${mw}" height="${mh}" `;
    s += `fill="none" stroke="green" stroke-width="${SW}"/>\n`;
    const inset = 10, r = 1.5;
    // Narożniki (mm, Y-up): TL, TR, BR, BL
    const corners: [number, number][] = [
      [-mw/2+inset,  mh/2-inset],
      [ mw/2-inset,  mh/2-inset],
      [ mw/2-inset, -mh/2+inset],
      [-mw/2+inset, -mh/2+inset],
    ];
    for (const [cx2, cy2] of corners)
      s += `  <circle cx="${sx(cx2)}" cy="${sy(cy2)}" r="${r}" fill="none" stroke="green" stroke-width="${SW}"/>\n`;
  }

  s += '</svg>\n';
  return s;
}

// ── Generowanie DXF (R2000, LWPOLYLINE) ───────────────────────
// Układ współrzędnych DXF: Y w górę (standardowe CAD).
// ContourPoint: Y w górę → bezpośrednie użycie x/y.
// Kolory DXF: 7=czarny(na białym tle), 1=czerwony, 3=zielony.
// Grubość linii (stała szerokość LWPOLYLINE): 0.5 mm.

function buildDXF(
  holeContours:  Contour[],
  outerContours: Contour[],
  imgWidthMm:    number,
  imgHeightMm:   number,
  machWidthMm:   number,
  machHeightMm:  number,
  frameEnabled:  boolean,
): string {
  const LW = 0.5; // stała szerokość linii (mm)
  let s = '0\nSECTION\n2\nHEADER\n0\nENDSEC\n0\nSECTION\n2\nENTITIES\n';

  // Zamknięta LWPOLYLINE (closed flag = 1)
  const addPoly = (pts: { x: number; y: number }[], color: number) => {
    s += `0\nLWPOLYLINE\n8\n0\n62\n${color}\n43\n${LW}\n70\n1\n90\n${pts.length}\n`;
    for (const p of pts) s += `10\n${p.x.toFixed(4)}\n20\n${p.y.toFixed(4)}\n`;
  };

  const addRect = (x0: number, y0: number, x1: number, y1: number, color: number) => {
    addPoly([{x:x0,y:y0},{x:x1,y:y0},{x:x1,y:y1},{x:x0,y:y1}], color);
  };

  const addCircle = (cx: number, cy: number, r: number, color: number) => {
    s += `0\nCIRCLE\n8\n0\n62\n${color}\n10\n${cx.toFixed(4)}\n20\n${cy.toFixed(4)}\n30\n0.0\n40\n${r.toFixed(4)}\n`;
  };

  // Ramka szablonu (czarna=7)
  addRect(-imgWidthMm/2, -imgHeightMm/2, imgWidthMm/2, imgHeightMm/2, 7);
  // Kontury otworów (czarne)
  for (const c of holeContours) addPoly(c.points, 7);
  // Zewnętrzny kontur kołnierza (czerwony=1)
  for (const c of outerContours) addPoly(c.points, 1);

  // Ramka maszyny + otwory na śruby (zielone=3)
  if (frameEnabled) {
    addRect(-machWidthMm/2, -machHeightMm/2, machWidthMm/2, machHeightMm/2, 3);
    const inset = 10, r = 1.5;
    const corners: [number, number][] = [
      [-machWidthMm/2+inset, -machHeightMm/2+inset],
      [ machWidthMm/2-inset, -machHeightMm/2+inset],
      [ machWidthMm/2-inset,  machHeightMm/2-inset],
      [-machWidthMm/2+inset,  machHeightMm/2-inset],
    ];
    for (const [cx2, cy2] of corners) addCircle(cx2, cy2, r, 3);
  }

  s += '0\nENDSEC\n0\nEOF\n';
  return s;
}

// ── Pobieranie pliku (helper) ─────────────────────────────────
function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.download = filename; a.href = url; a.click();
  URL.revokeObjectURL(url);
}

// ── State ─────────────────────────────────────────────────────

interface NormState {
  workingDataUrl: string | null;
  imgW: number;          // actual pixel dims of current working image
  imgH: number;
  pxW: string;           // editable pixel width field
  pxH: string;
  pxLocked: boolean;
  mmW: string;           // editable mm width field
  mmH: string;
  mmLocked: boolean;
  ppm: number | null;    // pixels per metre (derived from DPI or "Set mm" action)
  fitPx: string;         // value for "Fit to square" button
  fitMm: string;         // value for "Set dimensions to fit" button
  flangeWidthMm: string; // szerokość kołnierza (mm)
  leaveHoles: string;    // ile największych otworów zostawić (edytowalne)
  detectedHoles: number; // ile wykryto otworów przy ostatnim ładowaniu
  machineSizeX: string;  // rozmiar maszyny X (mm) – do ramki i pliku wektorowego
  machineSizeY: string;  // rozmiar maszyny Y (mm)
  frameEnabled: boolean; // czy dodać zielony prostokąt maszyny przy zapisie
  statusMsg: string;
}

const INIT: NormState = {
  workingDataUrl: null,
  imgW: 0, imgH: 0,
  pxW: '', pxH: '', pxLocked: true,
  mmW: '', mmH: '', mmLocked: true,
  ppm: null,
  fitPx: '2000',
  fitMm: '300',
  flangeWidthMm: '5',
  leaveHoles: '1',
  detectedHoles: 0,
  machineSizeX: '350',
  machineSizeY: '350',
  frameEnabled: false,
  statusMsg: 'Load a PNG file to begin.',
};

// ── Helpers ────────────────────────────────────────────────────

function r1(v: number)  { return Math.round(v * 10) / 10; }
function toMm(px: number, ppm: number) { return r1(px * 1000 / ppm); }

// ── Component ─────────────────────────────────────────────────

interface Props {
  onClose:                 () => void;
  onUseAsTemplate:         (dataUrl: string, widthMm: number, heightMm: number) => void;
  machineWidthMm:          number;   // z aktualnie wybranego typu maszyny w Parameters
  machineHeightMm:         number;
  initialTemplateDataUrl?: string | null;
  initialWidthMm?:         number;
  initialHeightMm?:        number;
}

const TemplateNormalizerDialog: React.FC<Props> = ({
  onClose, onUseAsTemplate, machineWidthMm, machineHeightMm,
  initialTemplateDataUrl, initialWidthMm, initialHeightMm,
}) => {
  const [s, setS] = useState<NormState>(() => ({
    ...INIT,
    machineSizeX: String(machineWidthMm),
    machineSizeY: String(machineHeightMm),
  }));
  const fileRef = useRef<HTMLInputElement>(null);

  const upd = (patch: Partial<NormState>) => setS(prev => ({ ...prev, ...patch }));

  // Wczytaj szablon przekazany z TemplateDesigner (lub aktualny szablon aplikacji)
  useEffect(() => {
    if (!initialTemplateDataUrl) return;
    const img = new Image();
    img.onload = async () => {
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      const wMm  = initialWidthMm;
      const hMm  = initialHeightMm;

      // Oblicz ppm z wymiarów mm (jeśli znane)
      let ppm: number | null = null;
      let mmW = '';
      let mmH = '';
      if (wMm && hMm && wMm > 0 && hMm > 0) {
        const longerPx = Math.max(imgW, imgH);
        const longerMm = Math.max(wMm, hMm);
        ppm = Math.round(longerPx * 1000 / longerMm);
        mmW = String(wMm);
        mmH = String(hMm);
      }

      const dpi = ppm ? Math.round(ppm * 25.4 / 1000) : 0;
      const dpiStr = dpi > 0 ? `  ·  DPI: ${dpi}` : '';
      const mmStr  = mmW && mmH ? `  ·  ${mmW}×${mmH} mm` : '';

      const holes = await countHolesInUrl(initialTemplateDataUrl);
      upd({
        workingDataUrl: initialTemplateDataUrl,
        imgW, imgH,
        pxW: String(imgW), pxH: String(imgH),
        mmW, mmH,
        ppm,
        leaveHoles: String(holes),
        detectedHoles: holes,
        statusMsg: `Loaded: ${imgW}×${imgH} px${dpiStr}${mmStr}`
          + (holes > 0 ? `  ·  ${holes} white region(s)` : '  ·  no white regions'),
      });
    };
    img.src = initialTemplateDataUrl;
  }, []); // eslint-disable-line

  // ── Load PNG ──────────────────────────────────────────────

  const handleLoad = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async ev => {
      const dataUrl = ev.target!.result as string;
      const buf = await file.arrayBuffer();
      const info = readPngInfo(new Uint8Array(buf));

      const ppm  = info.pixelsPerMetre;
      const imgW = info.widthPx;
      const imgH = info.heightPx;

      let mmW = '', mmH = '';
      let status: string;

      if (ppm && ppm > 0) {
        mmW = toMm(imgW, ppm).toFixed(1);
        mmH = toMm(imgH, ppm).toFixed(1);
        const dpi = Math.round(ppm * 25.4 / 1000);
        status = `Loaded: ${imgW}×${imgH} px  ·  DPI: ${dpi}  ·  ${mmW}×${mmH} mm`;
      } else {
        status = `Loaded: ${imgW}×${imgH} px  ·  No DPI metadata in file`;
      }

      // Policz spójne białe obszary (otwory) w załadowanym obrazie
      const holes = await countHolesInUrl(dataUrl);

      upd({
        workingDataUrl: dataUrl,
        imgW, imgH,
        pxW: String(imgW), pxH: String(imgH),
        mmW, mmH,
        ppm: ppm ?? null,
        leaveHoles: String(holes),
        detectedHoles: holes,
        statusMsg: status + (holes > 0 ? `  ·  ${holes} white region(s)` : '  ·  no white regions'),
      });
    };
    reader.readAsDataURL(file);
  }, []);

  // ── Px field changes (with aspect ratio lock) ─────────────

  const onPxW = (v: string) => {
    const n = parseInt(v);
    if (s.pxLocked && !isNaN(n) && n > 0 && s.imgW > 0) {
      upd({ pxW: v, pxH: String(Math.round(n * s.imgH / s.imgW)) });
    } else {
      upd({ pxW: v });
    }
  };
  const onPxH = (v: string) => {
    const n = parseInt(v);
    if (s.pxLocked && !isNaN(n) && n > 0 && s.imgH > 0) {
      upd({ pxH: v, pxW: String(Math.round(n * s.imgW / s.imgH)) });
    } else {
      upd({ pxH: v });
    }
  };

  // ── Mm field changes (with aspect ratio lock) ─────────────

  const onMmW = (v: string) => {
    const n = parseFloat(v);
    if (s.mmLocked && !isNaN(n) && n > 0 && s.imgW > 0) {
      upd({ mmW: v, mmH: r1(n * s.imgH / s.imgW).toFixed(1) });
    } else {
      upd({ mmW: v });
    }
  };
  const onMmH = (v: string) => {
    const n = parseFloat(v);
    if (s.mmLocked && !isNaN(n) && n > 0 && s.imgH > 0) {
      upd({ mmH: v, mmW: r1(n * s.imgW / s.imgH).toFixed(1) });
    } else {
      upd({ mmH: v });
    }
  };

  // ── Rescale to new px size ────────────────────────────────

  const handleRescalePx = useCallback(async () => {
    if (!s.workingDataUrl) return;
    const w = parseInt(s.pxW), h = parseInt(s.pxH);
    if (!w || !h || w < 1 || h < 1) { upd({ statusMsg: 'Invalid pixel dimensions.' }); return; }
    const newUrl = await rescaleToCanvas(s.workingDataUrl, w, h);
    let mmW = s.mmW, mmH = s.mmH;
    if (s.ppm) {
      mmW = toMm(w, s.ppm).toFixed(1);
      mmH = toMm(h, s.ppm).toFixed(1);
    }
    upd({ workingDataUrl: newUrl, imgW: w, imgH: h, mmW, mmH,
          statusMsg: `Rescaled to ${w}×${h} px` });
  }, [s]);

  // ── Set new mm dimensions ─────────────────────────────────

  const handleSetMm = useCallback(() => {
    if (!s.workingDataUrl || s.imgW === 0) return;
    const mmW = parseFloat(s.mmW), mmH = parseFloat(s.mmH);
    if (!mmW || !mmH || mmW <= 0 || mmH <= 0) {
      upd({ statusMsg: 'Invalid mm dimensions.' }); return;
    }
    const newPpm = Math.round(s.imgW * 1000 / mmW);
    const dpi    = Math.round(newPpm * 25.4 / 1000);
    upd({ ppm: newPpm,
          statusMsg: `mm size set: ${mmW.toFixed(1)}×${mmH.toFixed(1)} mm  ·  DPI: ${dpi}` });
  }, [s]);

  // ── Fit to square (px) ────────────────────────────────────

  const handleFitPx = useCallback(async () => {
    if (!s.workingDataUrl || s.imgW === 0) return;
    const side = parseInt(s.fitPx);
    if (!side || side < 1) { upd({ statusMsg: 'Invalid square side value.' }); return; }
    let w: number, h: number;
    if (s.imgW >= s.imgH) { w = side; h = Math.round(side * s.imgH / s.imgW); }
    else                  { h = side; w = Math.round(side * s.imgW / s.imgH); }
    const newUrl = await rescaleToCanvas(s.workingDataUrl, w, h);
    let mmW = s.mmW, mmH = s.mmH;
    if (s.ppm) {
      mmW = toMm(w, s.ppm).toFixed(1);
      mmH = toMm(h, s.ppm).toFixed(1);
    }
    upd({ workingDataUrl: newUrl, imgW: w, imgH: h,
          pxW: String(w), pxH: String(h), mmW, mmH,
          statusMsg: `Fitted to ${side} px square → ${w}×${h} px` });
  }, [s]);

  // ── Make square (px) ─────────────────────────────────────
  // Jak Fit to sqr, ale uzupełnia krótszy bok do kwadratu paskami.
  // Kolor paska: czerwony jeśli obraz zawiera piksele czerwone, inaczej czarny.

  const handleMakeSquare = useCallback(async () => {
    if (!s.workingDataUrl || s.imgW === 0) return;
    const side = parseInt(s.fitPx);
    if (!side || side < 1) { upd({ statusMsg: 'Invalid square side value.' }); return; }

    // Krok 1: przeskaluj do side px wzdłuż dłuższego boku
    let fw: number, fh: number;
    if (s.imgW >= s.imgH) { fw = side; fh = Math.round(side * s.imgH / s.imgW); }
    else                  { fh = side; fw = Math.round(side * s.imgW / s.imgH); }
    const fittedUrl = await rescaleToCanvas(s.workingDataUrl, fw, fh);

    // Krok 2: sprawdź czy są czerwone piksele w przeskalowanym obrazie
    const tmpImg = await loadImg(fittedUrl);
    const tmpC = document.createElement('canvas');
    tmpC.width = fw; tmpC.height = fh;
    const tmpCtx = tmpC.getContext('2d')!;
    tmpCtx.drawImage(tmpImg, 0, 0);
    const tmpId = tmpCtx.getImageData(0, 0, fw, fh);
    const tmpD = tmpId.data;
    let hasRed = false;
    for (let i = 0; i < fw * fh; i++) {
      if (classifyPixel(tmpD[i*4], tmpD[i*4+1], tmpD[i*4+2], tmpD[i*4+3]) === 2) {
        hasRed = true; break;
      }
    }

    // Krok 3: rysuj wyśrodkowany obraz na kwadracie side×side
    const sqC = document.createElement('canvas');
    sqC.width = side; sqC.height = side;
    const sqCtx = sqC.getContext('2d')!;
    // Wypełnij tło kolorem paska
    sqCtx.fillStyle = hasRed ? '#ff0000' : '#000000';
    sqCtx.fillRect(0, 0, side, side);
    // Wklej przeskalowany obraz wyśrodkowany
    const ox = Math.floor((side - fw) / 2);
    const oy = Math.floor((side - fh) / 2);
    sqCtx.drawImage(tmpImg, ox, oy, fw, fh);

    const newUrl = sqC.toDataURL('image/png');
    let mmW = s.mmW, mmH = s.mmH;
    if (s.ppm) {
      mmW = toMm(side, s.ppm).toFixed(1);
      mmH = toMm(side, s.ppm).toFixed(1);
    }
    upd({ workingDataUrl: newUrl, imgW: side, imgH: side,
          pxW: String(side), pxH: String(side), mmW, mmH,
          statusMsg: `Made square ${side}×${side} px (fill: ${hasRed ? 'red' : 'black'})` });
  }, [s]);

  // ── Set dimensions to fit (mm) ────────────────────────────

  const handleFitMm = useCallback(() => {
    if (!s.workingDataUrl || s.imgW === 0) return;
    const side = parseFloat(s.fitMm);
    if (!side || side <= 0) { upd({ statusMsg: 'Invalid mm value.' }); return; }
    let mmW: number, mmH: number;
    if (s.imgW >= s.imgH) { mmW = side; mmH = r1(side * s.imgH / s.imgW); }
    else                  { mmH = side; mmW = r1(side * s.imgW / s.imgH); }
    const newPpm = Math.round(s.imgW * 1000 / mmW);
    const dpi    = Math.round(newPpm * 25.4 / 1000);
    upd({ mmW: mmW.toFixed(1), mmH: mmH.toFixed(1), ppm: newPpm,
          statusMsg: `Fitted to ${side} mm square → ${mmW.toFixed(1)}×${mmH.toFixed(1)} mm  ·  DPI: ${dpi}` });
  }, [s]);

  // ── Leave holes only ──────────────────────────────────────
  // Zostawia tylko N największych białych obszarów; pozostałe zamalowuje
  // kolorem tła (czerwony jeśli obraz zawiera czerwone piksele, czarny w p.p.)

  const handleLeaveHolesOnly = useCallback(async () => {
    if (!s.workingDataUrl) return;
    const n = parseInt(s.leaveHoles);
    if (isNaN(n) || n < 0) { upd({ statusMsg: 'Invalid number of holes.' }); return; }

    const img = await loadImg(s.workingDataUrl);
    const w = img.naturalWidth, h = img.naturalHeight;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    const id = ctx.getImageData(0, 0, w, h);
    const d = id.data;

    // Sprawdź czy obraz ma piksele czerwone (klasa 2)
    let hasRed = false;
    for (let i = 0; i < w * h; i++) {
      if (classifyPixel(d[i*4], d[i*4+1], d[i*4+2], d[i*4+3]) === 2) { hasRed = true; break; }
    }
    const fillR = hasRed ? 255 : 0;
    const fillG = 0;
    const fillB = 0;

    const { labels, components } = labelWhiteComponents(d, w, h);

    // Ostrzeżenie gdy żąda się więcej otworów niż wykryto
    if (n > s.detectedHoles && s.detectedHoles > 0) {
      upd({ statusMsg: `Warning: only ${components.length} white region(s) found – showing all.` });
    }

    // Etykiety N największych komponentów do zachowania
    const keepLabels = new Set(components.slice(0, n).map(c => c.label));

    // Zamaluj piksele należące do pominiętych komponentów
    for (let i = 0; i < w * h; i++) {
      if (labels[i] === -1) continue;           // nie biały – nie dotykaj
      if (keepLabels.has(labels[i])) continue;  // zachowany otwór
      // Zamaluj na kolor tła
      d[i*4] = fillR; d[i*4+1] = fillG; d[i*4+2] = fillB; d[i*4+3] = 255;
    }

    ctx.putImageData(id, 0, 0);
    const newUrl = c.toDataURL('image/png');
    const kept = Math.min(n, components.length);
    upd({
      workingDataUrl: newUrl,
      leaveHoles: String(kept),
      detectedHoles: kept,
      statusMsg: `Left ${kept} largest hole(s) of ${components.length} found (fill: ${hasRed ? 'red' : 'black'}).`,
    });
  }, [s]);

  // ── Normalize colors → B-W ────────────────────────────────

  const handleNormalizeBW = useCallback(async () => {
    if (!s.workingDataUrl) return;
    const newUrl = await normalizeToBlackWhite(s.workingDataUrl);
    upd({ workingDataUrl: newUrl, statusMsg: 'Colors normalized to black & white.' });
  }, [s.workingDataUrl]);

  // ── Normalize colors → R-B-W ─────────────────────────────

  const handleNormalizeRBW = useCallback(async () => {
    if (!s.workingDataUrl) return;
    const newUrl = await normalizeToRedBlackWhite(s.workingDataUrl);
    upd({ workingDataUrl: newUrl, statusMsg: 'Colors normalized to red / black / white.' });
  }, [s.workingDataUrl]);

  // ── Create flange ─────────────────────────────────────────

  const handleCreateFlange = useCallback(async () => {
    if (!s.workingDataUrl) return;

    // Przelicz mm → piksele (potrzebne PPM)
    let ppm = s.ppm;
    if (!ppm && s.mmW) {
      const mmW = parseFloat(s.mmW);
      if (mmW > 0) ppm = Math.round(s.imgW * 1000 / mmW);
    }
    if (!ppm || ppm <= 0) {
      upd({ statusMsg: 'Set mm dimensions first (needed for mm→px conversion).' });
      return;
    }

    const flangeWidthMm = parseFloat(s.flangeWidthMm);
    if (!flangeWidthMm || flangeWidthMm <= 0) {
      upd({ statusMsg: 'Invalid flange width.' });
      return;
    }

    const flangeWidthPx = flangeWidthMm * ppm / 1000;
    upd({ statusMsg: `Creating flange (${flangeWidthMm} mm = ${flangeWidthPx.toFixed(1)} px)…` });

    const newUrl = await createFlange(s.workingDataUrl, flangeWidthPx);
    upd({ workingDataUrl: newUrl,
          statusMsg: `Flange created: ${flangeWidthMm} mm  ·  ${flangeWidthPx.toFixed(1)} px` });
  }, [s]);

  // ── Set machine size (z aktualnego type'u maszyny) ────────

  const handleSetMachineSize = useCallback(() => {
    upd({ machineSizeX: String(machineWidthMm), machineSizeY: String(machineHeightMm),
          statusMsg: `Machine size set: ${machineWidthMm} × ${machineHeightMm} mm` });
  }, [machineWidthMm, machineHeightMm]);

  // ── Save PNG ──────────────────────────────────────────────

  /** Buduje finalny PNG (z opcjonalną ramką i DPI). Zwraca null jeśli nie ma obrazu. */
  const buildFinalPng = useCallback(async (): Promise<{
    blob: Blob; widthMm: number; heightMm: number; canvasW: number; canvasH: number; ppm: number | null;
  } | null> => {
    if (!s.workingDataUrl) return null;

    const img = await loadImg(s.workingDataUrl);
    const imgW = img.naturalWidth, imgH = img.naturalHeight;

    let ppm = s.ppm;
    if (!ppm) {
      const mmW = parseFloat(s.mmW);
      if (mmW > 0) ppm = Math.round(imgW * 1000 / mmW);
    }

    let canvasW = imgW, canvasH = imgH;
    let drawOffX = 0, drawOffY = 0;
    let greenW = 0, greenH = 0, greenX = 0, greenY = 0;
    if (s.frameEnabled && ppm) {
      const machW = parseFloat(s.machineSizeX);
      const machH = parseFloat(s.machineSizeY);
      if (machW > 0 && machH > 0) {
        const mPxW = Math.round(machW * ppm / 1000);
        const mPxH = Math.round(machH * ppm / 1000);
        canvasW  = Math.max(imgW, mPxW);
        canvasH  = Math.max(imgH, mPxH);
        drawOffX = Math.floor((canvasW - imgW) / 2);
        drawOffY = Math.floor((canvasH - imgH) / 2);
        greenW   = mPxW; greenH = mPxH;
        greenX   = Math.floor((canvasW - mPxW) / 2);
        greenY   = Math.floor((canvasH - mPxH) / 2);
      }
    } else if (s.frameEnabled && !ppm) {
      upd({ statusMsg: 'Frame requires mm dimensions – set them first.' });
      return null;
    }

    const c = document.createElement('canvas');
    c.width = canvasW; c.height = canvasH;
    const ctx = c.getContext('2d')!;
    if (greenW > 0) { ctx.fillStyle = '#00ff00'; ctx.fillRect(greenX, greenY, greenW, greenH); }
    ctx.drawImage(img, drawOffX, drawOffY);

    let pngBytes = base64ToBytes(c.toDataURL('image/png').split(',')[1]);
    if (ppm && ppm > 0) pngBytes = setPngDpi(pngBytes, ppm, ppm);

    const blob = new Blob([pngBytes], { type: 'image/png' });
    const widthMm  = ppm ? Math.round(canvasW * 1000 / ppm * 10) / 10 : parseFloat(s.mmW)  || 0;
    const heightMm = ppm ? Math.round(canvasH * 1000 / ppm * 10) / 10 : parseFloat(s.mmH) || 0;

    return { blob, widthMm, heightMm, canvasW, canvasH, ppm };
  }, [s]);

  const handleSave = useCallback(async () => {
    const result = await buildFinalPng();
    if (!result) { if (s.workingDataUrl === null) upd({ statusMsg: 'Nothing to save.' }); return; }
    const { blob, canvasW, canvasH, ppm } = result;
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.download = 'template_normalized.png'; a.href = url; a.click();
    URL.revokeObjectURL(url);
    const dpiInfo = ppm ? `  ·  DPI: ${Math.round(ppm * 25.4 / 1000)}` : '';
    upd({ statusMsg: `Saved PNG: ${canvasW}×${canvasH} px${dpiInfo}` });
  }, [s.workingDataUrl, buildFinalPng]);

  const handleUseAsTemplate = useCallback(async () => {
    const result = await buildFinalPng();
    if (!result) return;
    const { blob, widthMm, heightMm } = result;
    const dataUrl = await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target!.result as string);
      reader.readAsDataURL(blob);
    });
    onUseAsTemplate(dataUrl, widthMm, heightMm);
    onClose();
  }, [buildFinalPng, onUseAsTemplate, onClose]);

  // ── Wspólna walidacja mm + ekstrakcja konturów ────────────

  const getContoursForExport = useCallback(async (): Promise<{
    holeContours: Contour[]; outerContours: Contour[];
    imgWidthMm: number; imgHeightMm: number;
    machWidthMm: number; machHeightMm: number;
  } | null> => {
    if (!s.workingDataUrl) { upd({ statusMsg: 'Nothing to export.' }); return null; }
    const imgWidthMm  = parseFloat(s.mmW);
    const imgHeightMm = parseFloat(s.mmH);
    if (!(imgWidthMm > 0 && imgHeightMm > 0)) {
      upd({ statusMsg: 'Set mm dimensions before exporting SVG/DXF.' }); return null;
    }
    const machWidthMm  = parseFloat(s.machineSizeX);
    const machHeightMm = parseFloat(s.machineSizeY);
    if (s.frameEnabled && !(machWidthMm > 0 && machHeightMm > 0)) {
      upd({ statusMsg: 'Invalid machine size.' }); return null;
    }
    upd({ statusMsg: 'Extracting contours…' });
    const { holeContours, outerContours } =
      await extractImageContours(s.workingDataUrl, imgWidthMm, imgHeightMm);
    return { holeContours, outerContours, imgWidthMm, imgHeightMm, machWidthMm, machHeightMm };
  }, [s]);

  // ── Save SVG ──────────────────────────────────────────────

  const handleSaveSVG = useCallback(async () => {
    const data = await getContoursForExport();
    if (!data) return;
    const { holeContours, outerContours, imgWidthMm, imgHeightMm, machWidthMm, machHeightMm } = data;
    const svg = buildSVG(holeContours, outerContours,
      imgWidthMm, imgHeightMm, machWidthMm, machHeightMm, s.frameEnabled);
    downloadBlob(svg, 'template.svg', 'image/svg+xml');
    upd({ statusMsg: `SVG saved (${holeContours.length} hole(s), ${outerContours.length} outer contour(s)).` });
  }, [s, getContoursForExport]);

  // ── Save DXF ──────────────────────────────────────────────

  const handleSaveDXF = useCallback(async () => {
    const data = await getContoursForExport();
    if (!data) return;
    const { holeContours, outerContours, imgWidthMm, imgHeightMm, machWidthMm, machHeightMm } = data;
    const dxf = buildDXF(holeContours, outerContours,
      imgWidthMm, imgHeightMm, machWidthMm, machHeightMm, s.frameEnabled);
    downloadBlob(dxf, 'template.dxf', 'application/dxf');
    upd({ statusMsg: `DXF saved (${holeContours.length} hole(s), ${outerContours.length} outer contour(s)).` });
  }, [s, getContoursForExport]);

  // ── Lock button ───────────────────────────────────────────

  const LockBtn = ({ locked, onToggle }: { locked: boolean; onToggle: () => void }) => (
    <button className="norm-lock-btn" onClick={onToggle} title={locked ? 'Locked (click to unlock)' : 'Unlocked (click to lock)'}>
      {locked ? '🔒' : '🔓'}
    </button>
  );

  // ── Size row helper ───────────────────────────────────────

  const SizeRow = ({
    vW, vH, onW, onH, locked, onLock, unit, isInt,
  }: {
    vW: string; vH: string;
    onW: (v: string) => void; onH: (v: string) => void;
    locked: boolean; onLock: () => void;
    unit: string; isInt: boolean;
  }) => (
    <div className="norm-size-row">
      <span className="norm-size-label">X</span>
      <input
        type="number"
        className="norm-num-input"
        value={vW}
        min={isInt ? 1 : 0.1}
        step={isInt ? 1 : 0.1}
        onChange={e => onW(e.target.value)}
      />
      <span className="norm-unit">{unit}</span>
      <LockBtn locked={locked} onToggle={onLock} />
      <span className="norm-size-label">Y</span>
      <input
        type="number"
        className="norm-num-input"
        value={vH}
        min={isInt ? 1 : 0.1}
        step={isInt ? 1 : 0.1}
        onChange={e => onH(e.target.value)}
      />
      <span className="norm-unit">{unit}</span>
    </div>
  );

  const hasImg = !!s.workingDataUrl;

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="layers-overlay">
      <div className="norm-dialog">

        {/* ── Lewa kolumna: kontrolki ── */}
        <div className="norm-left">

          {/* 1. Load PNG */}
          <button style={{ width: '100%' }} onClick={() => fileRef.current?.click()}>
            Load PNG
          </button>
          <input ref={fileRef} type="file" accept="image/png"
            style={{ display: 'none' }} onChange={handleLoad} />

          <div className="norm-separator" />

          {/* 2. SizeX/Y px + lock */}
          <SizeRow
            vW={s.pxW} vH={s.pxH}
            onW={onPxW} onH={onPxH}
            locked={s.pxLocked}
            onLock={() => upd({ pxLocked: !s.pxLocked })}
            unit="px" isInt={true}
          />

          {/* 3. Rescale */}
          <button style={{ width: '100%' }} disabled={!hasImg} onClick={handleRescalePx}>
            Rescale to new px size
          </button>

          <div className="norm-separator" />

          {/* 4. SizeX/Y mm + lock */}
          <SizeRow
            vW={s.mmW} vH={s.mmH}
            onW={onMmW} onH={onMmH}
            locked={s.mmLocked}
            onLock={() => upd({ mmLocked: !s.mmLocked })}
            unit="mm" isInt={false}
          />

          {/* 5. Set new mm dimensions */}
          <button style={{ width: '100%' }} disabled={!hasImg} onClick={handleSetMm}>
            Set new mm dimensions
          </button>

          <div className="norm-separator" />

          {/* 6. Fit to sqr / Make sqr px */}
          <div className="norm-fit-row">
            <button disabled={!hasImg} onClick={handleFitPx}>
              Fit to sqr
            </button>
            <button disabled={!hasImg} onClick={handleMakeSquare} style={{ marginLeft: 4 }}>
              Make sqr
            </button>
            <input
              type="number" className="norm-fit-input"
              value={s.fitPx} min={1} step={1}
              onChange={e => upd({ fitPx: e.target.value })}
            />
            <span className="norm-unit">px</span>
          </div>

          {/* 7. Set dimensions to fit mm */}
          <div className="norm-fit-row">
            <button disabled={!hasImg} onClick={handleFitMm}>
              Set dimensions to fit
            </button>
            <input
              type="number" className="norm-fit-input"
              value={s.fitMm} min={0.1} step={0.1}
              onChange={e => upd({ fitMm: e.target.value })}
            />
            <span className="norm-unit">mm</span>
          </div>

          <div className="norm-separator" />

          {/* 8. Create flange */}
          <div className="norm-fit-row">
            <button disabled={!hasImg} onClick={handleCreateFlange}>
              Create flange
            </button>
            <input
              type="number" className="norm-fit-input"
              value={s.flangeWidthMm} min={0.1} step={0.1}
              onChange={e => upd({ flangeWidthMm: e.target.value })}
            />
            <span className="norm-unit">mm</span>
          </div>

          {/* 9. Leave holes only */}
          <div className="norm-fit-row">
            <button
              disabled={!hasImg}
              onClick={handleLeaveHolesOnly}
              title="Keep only the N largest white regions; fill others with red or black"
            >
              Leave holes only
            </button>
            <input
              type="number" className="norm-fit-input"
              value={s.leaveHoles} min={0} step={1}
              onChange={e => upd({ leaveHoles: e.target.value })}
              style={{ width: 48 }}
            />
          </div>

          {/* 10. Normalize colors – dwa przyciski w jednej linijce */}
          <div className="norm-fit-row">
            <button
              style={{ flex: 1 }}
              disabled={!hasImg}
              onClick={handleNormalizeBW}
              title="Reduce to black & white (threshold near white)"
            >
              To B-W
            </button>
            <button
              style={{ flex: 1, marginLeft: 4 }}
              disabled={!hasImg}
              onClick={handleNormalizeRBW}
              title="Reduce to red / black / white (same thresholds as vectorizer)"
            >
              To R-B-W
            </button>
          </div>

          <div style={{ height: 4 }} />

          {/* 11. Machine size + Frame */}
          <div className="norm-fit-row" style={{ flexWrap: 'wrap', gap: 4 }}>
            <button onClick={handleSetMachineSize} title="Reset to machine size from Parameters">
              Set machine size
            </button>
            <input
              type="number" className="norm-fit-input" style={{ width: 52 }}
              value={s.machineSizeX} min={1} step={1}
              onChange={e => upd({ machineSizeX: e.target.value })}
              title="Machine width (mm)"
            />
            <span className="norm-unit">×</span>
            <input
              type="number" className="norm-fit-input" style={{ width: 52 }}
              value={s.machineSizeY} min={1} step={1}
              onChange={e => upd({ machineSizeY: e.target.value })}
              title="Machine height (mm)"
            />
            <span className="norm-unit">mm</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={s.frameEnabled}
                onChange={e => upd({ frameEnabled: e.target.checked })} />
              Frame
            </label>
          </div>

          {/* 12. Save PNG / SVG / DXF */}
          <div className="norm-fit-row">
            <button style={{ flex: 1 }} disabled={!hasImg} onClick={handleSave}>
              Save PNG
            </button>
            <button style={{ flex: 1, marginLeft: 4 }} disabled={!hasImg} onClick={handleSaveSVG}>
              Save SVG
            </button>
            <button style={{ flex: 1, marginLeft: 4 }} disabled={!hasImg} onClick={handleSaveDXF}>
              Save DXF
            </button>
          </div>

          {/* Status + Close */}
          <div className="norm-spacer" />
          <div className="norm-status">{s.statusMsg}</div>
          <div style={{ height: 4 }} />
          <button style={{ width: '100%' }} disabled={!hasImg} onClick={handleUseAsTemplate}>
            Use as templ and close
          </button>
          <div style={{ height: 4 }} />
          <button style={{ width: '100%' }} onClick={onClose}>Close</button>
        </div>

        {/* ── Prawa kolumna: podgląd ── */}
        <div className="norm-right">
          {s.workingDataUrl
            ? <img src={s.workingDataUrl} alt="preview" className="norm-preview" />
            : <div className="norm-preview-empty">No image loaded</div>
          }
          {hasImg && (
            <div className="tpl-info">
              {s.imgW}&thinsp;×&thinsp;{s.imgH}&thinsp;px
              {s.mmW && s.mmH
                ? <>&emsp;·&emsp;{s.mmW}&thinsp;×&thinsp;{s.mmH}&thinsp;mm</>
                : null}
              {s.ppm
                ? <>&emsp;·&emsp;{Math.round(s.ppm * 25.4 / 1000)}&thinsp;DPI</>
                : null}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TemplateNormalizerDialog;
